"use client";

import React, { useState, useContext, useEffect } from "react";
import {
  IconButton,
  Typography,
  Box,
  Tooltip,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
} from "@mui/material";
import Logout from "@mui/icons-material/Logout";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { AuthContext } from "../../../authentication/AuthContext";
import config from "../../../config";
import { useProfileRefresh } from "../../../hooks/useProfileRefresh";
import { useNavigate } from "react-router-dom";

const Profile = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const open = Boolean(anchorEl);

  // Update profile data when user changes or config updates
  useEffect(() => {
    if (user) {
      setProfileData(user);
    } else if (config.user) {
      setProfileData(config.user);
    }
  }, [user]);

  // Listen for profile picture updates in real-time
  useProfileRefresh((updatedData) => {
    setProfileData(updatedData);
  });

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
  };

  const firstName = profileData?.first_name || "Admin";
  const lastName = profileData?.last_name || "";
  const userName = profileData?.user_name || "Admin";
  const profilePicUrl = profileData?.profile_picture || "/images/admin.png";

  return (
    <>
      <Tooltip title="Account settings">
        <IconButton
          onClick={handleClick}
          size="small"
          sx={{ p: 0, borderRadius: "5px" }}
          aria-controls={open ? "account-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
        >
          <Avatar
            src={profilePicUrl}
            alt={firstName}
            sx={{
              width: { xs: "35px", sm: "42px" },
              height: { xs: "35px", sm: "42px" },
              border: "2px solid #C2CDFF",
            }}
            className="mr-8"
          />
          <Typography
            variant="h3"
            sx={{
              fontWeight: "600",
              fontSize: "13px",
              display: { xs: "none", sm: "block" },
            }}
            className="text-black"
          >
            {userName}
          </Typography>
          <KeyboardArrowDownIcon sx={{ fontSize: "15px" }} />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: "7px",
            boxShadow: "0 4px 45px #0000001a",
            overflow: "visible",
            mt: 1.5,
            "& .MuiAvatar-root": {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            "&:before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: "background.paper",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        className="for-dark-top-navList"
      >
        <MenuItem
          sx={{ padding: "10px 20px", cursor: "pointer" }}
          onClick={() => {
            handleClose();
            navigate("/edit-profile");
          }}
        >
          <Avatar
            src={profilePicUrl}
            sx={{
              width: 31,
              height: 31,
              border: "2px solid #C2CDFF",
            }}
            className="mr-8"
          />
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontSize: "13px",
                color: "#260944",
                fontWeight: "500",
              }}
              className="text-black"
            >
              {firstName} {lastName}
            </Typography>

            <Typography sx={{ fontSize: "12px" }}>{profileData?.role || "Admin"}</Typography>
          </Box>
        </MenuItem>

        <Divider sx={{ borderColor: "#F6F7F9" }} />

        {/* Settings button - commented out */}
        {/* <MenuItem sx={{ padding: "8px 20px" }}>
          <Link
            to="/settings/account-settings"
            className={`text-black ${
              pathname === "/settings/account-settings" ? `text-primary` : ""
            }`}
            style={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <ListItemIcon sx={{ mr: "-10px", mt: "-3px" }}>
              <SettingsIcon sx={{ fontSize: "20px" }} className="text-black" />
            </ListItemIcon>

            <span style={{ fontSize: "13px" }}>Settings</span>
          </Link>
        </MenuItem> */}

        <MenuItem sx={{ padding: "8px 20px" }} onClick={handleLogout}>
          <ListItemIcon sx={{ mr: "-10px", mt: "-3px" }}>
            <Logout sx={{ fontSize: "20px" }} className="text-black" />
          </ListItemIcon>
          <span style={{ fontSize: "13px" }}>Logout</span>
        </MenuItem>
      </Menu>
    </>
  );
};

export default Profile;
